/* eslint-disable react/no-access-state-in-setstate */

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import * as React from 'react';
import { connect } from 'react-redux';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { Dispatch } from 'redux';

import { createModalCloseAction, createModalOpenAction } from '../../actions/modalActions';
import { ModalId } from '../../constants/modalId';
import { ClippingPlatformState } from '../../store/clippingPlatformState';
import Button from '../buttons/button';
import {
  baseMapDispatchToProps,
  baseMapStateToProps,
  ComponentBase,
  BaseDispatchProps,
  BaseStateProps,
} from '../componentBase';

import styles from './appModal.module.scss';

export interface AppModalOwnProps {
  title: string;
  id: ModalId;
  buttonIcon?: IconProp;
  submitAction?: () => void;
  hideOpenButton?: boolean;
  showSendAnotherCheckbox?: boolean;
  invertedButtonColours?: boolean;
  smallButton?: boolean;
  onClosed?: () => void;
  className?: string;
  closeButtonLabel?: string;
  onClose?: () => void;
  submitButtonLabel?: string;
  hideActionButtons?: boolean;
  disabled?: boolean;
  submitDisabled?: boolean;
  form?: string;
}

interface AppModalStateProps extends BaseStateProps {
  isOpen: boolean;
}

interface AppModalDispatchProps extends BaseDispatchProps {
  updateModalOpenState: (state: boolean) => void;
}

interface AppModalState {
  hideButtons: boolean;
  sendAnother: boolean;
}

class AppModal extends ComponentBase<AppModalOwnProps, AppModalStateProps, AppModalDispatchProps, AppModalState> {
  private checkbox: JSX.Element;

  constructor(props: any) {
    super(props);
    this.state = {
      hideButtons: false,
      sendAnother: false,
    };
    this.toggleIsOpen = this.toggleIsOpen.bind(this);
    this.close = this.close.bind(this);
    this.onCheckboxUpdate = this.onCheckboxUpdate.bind(this);
  }

  public loggedInRender() {
    return (
      <div className={styles.buttonWrapper}>
        {!this.props.hideOpenButton && (
          <Button
            type="button"
            icon={this.props.buttonIcon}
            onClick={this.toggleIsOpen}
            allCapitals
            inverseColours={this.props.invertedButtonColours}
            slimButton={this.props.smallButton}
            text={this.props.title}
            disabled={this.props.disabled}
          />
        )}
        <Modal
          isOpen={this.props.isOpen}
          toggle={this.toggleIsOpen}
          className={styles.appModal}
          onClosed={this.props.onClosed}
        >
          <ModalHeader toggle={this.toggleIsOpen}>{this.props.title}</ModalHeader>
          <ModalBody>{this.props.children}</ModalBody>
          <ModalFooter>{!this.state.hideButtons && this.renderButtons()}</ModalFooter>
        </Modal>
      </div>
    );
  }

  public toggleIsOpen() {
    this.props.updateModalOpenState(!this.props.isOpen);
  }

  public hideButtons() {
    this.setState({
      ...this.state,
      hideButtons: true,
    });
  }

  public showButtons() {
    this.setState({
      ...this.state,
      hideButtons: false,
    });
  }

  public close() {
    if (this.props.onClose) {
      this.props.onClose();
    }

    if (this.props.isOpen) {
      this.props.updateModalOpenState(false);
    }
  }

  private renderButtons() {
    return this.renderSubmitCancelButtons();
  }

  private renderCloseButton(): JSX.Element {
    return (
      <Button
        onClick={this.close}
        className={styles.cancel}
        text={this.props.closeButtonLabel || 'Cancel'}
        allCapitals
      />
    );
  }

  private renderSubmitCancelButtons(): JSX.Element {
    if (this.props.hideActionButtons) {
      return null;
    }

    this.checkbox = (
      <>
        Add another &nbsp;
        <input type="checkbox" name="Add another" onChange={this.onCheckboxUpdate} checked={this.state.sendAnother} />
      </>
    );

    return (
      <>
        {!!this.props.submitAction && this.props.showSendAnotherCheckbox && this.checkbox}
        {this.renderCloseButton()}
        {!!this.props.submitAction && this.renderSubmitButton()}
        {this.props.form && this.renderFormSubmitButton()}
      </>
    );
  }

  private onCheckboxUpdate() {
    this.setState({ sendAnother: !this.state.sendAnother });
  }

  private renderSubmitButton(): JSX.Element {
    return (
      <Button
        onClick={this.props.submitAction}
        className={styles.submit}
        text={this.props.submitButtonLabel || 'Submit'}
        allCapitals
        disabled={this.props.submitDisabled}
      />
    );
  }

  private renderFormSubmitButton(): JSX.Element {
    return (
      <Button
        type="submit"
        form={this.props.form}
        className={styles.submit}
        text={this.props.submitButtonLabel || 'Submit'}
        allCapitals
      />
    );
  }
}

function mapStateToProps(state: ClippingPlatformState, ownProps: AppModalOwnProps): AppModalStateProps {
  return {
    ...baseMapStateToProps(state),
    isOpen: state.appStatus.modals[ownProps.id] ? state.appStatus.modals[ownProps.id].open : false,
  };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: AppModalOwnProps): AppModalDispatchProps {
  return {
    ...baseMapDispatchToProps(dispatch),
    updateModalOpenState: (newState: boolean) =>
      dispatch(newState ? createModalOpenAction(ownProps.id) : createModalCloseAction(ownProps.id)),
  };
}

export default (connect(mapStateToProps, mapDispatchToProps, null, {
  withRef: true,
})(AppModal) as unknown) as React.ComponentClass<AppModalOwnProps>;
